'use client';

import { useState } from 'react';
import Link from 'next/link';
import { trackLead } from '@/lib/analytics';

type Step = 1 | 2 | 3 | 4 | 5;

interface FormData {
  gender: 'mann' | 'kvinne' | '';
  age: string;
  height_cm: string;
  weight_kg: string;
  goal: string;
  activity_level: string;
  allergies: string[];
  preferences: string[];
  email: string;
  name: string;
  newsletter_consent: boolean;
}

interface MealItem {
  type: string;
  targetKcal: number;
  recipe: { slug: string; name: string; calories: number; protein: number; image: string; portions: number } | null;
  mealKcal: number;
  mealProtein: number;
}

interface DayPlan {
  day: string;
  meals: MealItem[];
  totalKcal: number;
  totalProtein: number;
}

interface Results {
  bmr: number;
  tdee: number;
  targetCalories: number;
  protein: number;
  carbs: number;
  fat: number;
  mealPlan?: DayPlan[];
}

const GOALS = [
  { id: 'ned_vekt', label: 'Gå ned i vekt', desc: '500 kcal underskudd' },
  { id: 'opprettholde', label: 'Opprettholde vekt', desc: 'Balansert inntak' },
  { id: 'opp_vekt', label: 'Gå opp i vekt', desc: '300 kcal overskudd' },
  { id: 'muskelmasse', label: 'Bygge muskelmasse', desc: '250 kcal lean bulk' },
];

const ACTIVITIES = [
  { id: 'stillesittende', label: 'Stillesittende', desc: 'Kontorjobb, lite trening' },
  { id: 'lett_aktiv', label: 'Lett aktiv', desc: 'Lett trening 1-3 dager/uke' },
  { id: 'moderat_aktiv', label: 'Moderat aktiv', desc: 'Trening 3-5 dager/uke' },
  { id: 'svaert_aktiv', label: 'Svært aktiv', desc: 'Hard trening 6-7 dager/uke' },
  { id: 'ekstremt_aktiv', label: 'Ekstremt aktiv', desc: 'Fysisk arbeid + daglig trening' },
];

const ALLERGIES = ['Gluten', 'Laktose', 'Nøtter', 'Egg', 'Skalldyr', 'Soya', 'Fisk'];
const PREFS = ['Ikke svin', 'Ikke rødt kjøtt', 'Vegetar', 'Vegansk', 'Pescetar'];

export function UkeplanKalkulator() {
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<FormData>({
    gender: '', age: '', height_cm: '', weight_kg: '',
    goal: '', activity_level: '',
    allergies: [], preferences: [],
    email: '', name: '', newsletter_consent: false,
  });
  const [results, setResults] = useState<Results | null>(null);
  const [loading, setLoading] = useState(false);

  const update = (field: keyof FormData, value: string | boolean | string[]) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleArray = (field: 'allergies' | 'preferences', value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value],
    }));
  };

  const canNext = (): boolean => {
    if (step === 1) return !!form.gender && !!form.age && !!form.height_cm && !!form.weight_kg;
    if (step === 2) return !!form.goal;
    if (step === 3) return !!form.activity_level;
    if (step === 4) return true;
    return false;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/ukeplan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          age: Number(form.age),
          height_cm: Number(form.height_cm),
          weight_kg: Number(form.weight_kg),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setResults(data.results);
        setStep(5 as Step);
        // Lead-konvertering for ads/analytics — kun når e-post faktisk er oppgitt (da lagres leaden)
        if (form.email.trim()) {
          trackLead('generate_lead', { content_name: 'meal_plan', method: 'ukeplan-kalkulator' });
        }
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  return (
    <main className="min-h-screen gradient-warm">
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-10">
        {/* Breadcrumb */}
        <nav className="text-xs text-stone-400 mb-8 flex items-center gap-1">
          <Link href="/" className="hover:text-stone-600">Hjem</Link>
          <span>&rsaquo;</span>
          <span className="text-stone-600">Ukeplan-kalkulator</span>
        </nav>

        <h1 className="text-2xl font-semibold text-stone-900 mb-2">Ukeplan-kalkulator</h1>
        <p className="text-stone-500 text-sm mb-8">Beregn ditt daglige behov og få en personlig kostholdsplan.</p>

        {/* Progress */}
        {(step as number) < 5 && (
          <div className="flex gap-1 mb-8">
            {[1,2,3,4].map(s => (
              <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= (step as number) ? 'bg-stone-900' : 'bg-stone-200'}`} />
            ))}
          </div>
        )}

        {/* Step 1: Basic info */}
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="font-semibold text-stone-900 text-sm">Steg 1: Grunnleggende info</h2>
            <div className="flex gap-3">
              {(['mann', 'kvinne'] as const).map(g => (
                <button key={g} onClick={() => update('gender', g)}
                  className={`flex-1 py-3 rounded-xl text-sm font-medium border transition-colors ${form.gender === g ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'}`}>
                  {g === 'mann' ? 'Mann' : 'Kvinne'}
                </button>
              ))}
            </div>
            {[
              { key: 'age' as const, label: 'Alder', placeholder: '30', suffix: 'år' },
              { key: 'height_cm' as const, label: 'Høyde', placeholder: '175', suffix: 'cm' },
              { key: 'weight_kg' as const, label: 'Vekt', placeholder: '80', suffix: 'kg' },
            ].map(f => (
              <div key={f.key}>
                <label className="text-xs text-stone-500 block mb-1">{f.label}</label>
                <div className="flex items-center gap-2">
                  <input type="number" value={form[f.key]} onChange={e => update(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    className="flex-1 border border-stone-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-stone-400" />
                  <span className="text-xs text-stone-400 w-8">{f.suffix}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Step 2: Goal */}
        {step === 2 && (
          <div className="space-y-3">
            <h2 className="font-semibold text-stone-900 text-sm">Steg 2: Hva er målet ditt?</h2>
            {GOALS.map(g => (
              <button key={g.id} onClick={() => update('goal', g.id)}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${form.goal === g.id ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'}`}>
                <div className="text-sm font-medium">{g.label}</div>
                <div className={`text-xs ${form.goal === g.id ? 'text-stone-300' : 'text-stone-400'}`}>{g.desc}</div>
              </button>
            ))}
          </div>
        )}

        {/* Step 3: Activity */}
        {step === 3 && (
          <div className="space-y-3">
            <h2 className="font-semibold text-stone-900 text-sm">Steg 3: Aktivitetsnivå</h2>
            {ACTIVITIES.map(a => (
              <button key={a.id} onClick={() => update('activity_level', a.id)}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${form.activity_level === a.id ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'}`}>
                <div className="text-sm font-medium">{a.label}</div>
                <div className={`text-xs ${form.activity_level === a.id ? 'text-stone-300' : 'text-stone-400'}`}>{a.desc}</div>
              </button>
            ))}
          </div>
        )}

        {/* Step 4: Allergies + Email */}
        {step === 4 && (
          <div className="space-y-6">
            <div>
              <h2 className="font-semibold text-stone-900 text-sm mb-3">Allergier (valgfritt)</h2>
              <div className="flex flex-wrap gap-2">
                {ALLERGIES.map(a => (
                  <button key={a} onClick={() => toggleArray('allergies', a.toLowerCase())}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form.allergies.includes(a.toLowerCase()) ? 'bg-red-50 border-red-300 text-red-700' : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}>
                    {a}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h2 className="font-semibold text-stone-900 text-sm mb-3">Preferanser (valgfritt)</h2>
              <div className="flex flex-wrap gap-2">
                {PREFS.map(p => (
                  <button key={p} onClick={() => toggleArray('preferences', p.toLowerCase())}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form.preferences.includes(p.toLowerCase()) ? 'bg-green-50 border-green-300 text-green-700' : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h2 className="font-semibold text-stone-900 text-sm mb-3">Få resultatet på e-post (valgfritt)</h2>
              <input type="text" placeholder="Navn" value={form.name} onChange={e => update('name', e.target.value)}
                className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-stone-400 mb-2" />
              <input type="email" placeholder="din@epost.no" value={form.email} onChange={e => update('email', e.target.value)}
                className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-stone-400" />
              <label className="flex items-start gap-2 mt-3 cursor-pointer">
                <input type="checkbox" checked={form.newsletter_consent} onChange={e => update('newsletter_consent', e.target.checked)} className="mt-0.5" />
                <span className="text-xs text-stone-500">Ja, send meg oppskriftstips ukentlig</span>
              </label>
            </div>
          </div>
        )}

        {/* Step 5: Results */}
        {step === 5 && results && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                <span className="text-green-600 text-xl">✓</span>
              </div>
              <h2 className="text-lg font-semibold text-stone-900">Ditt daglige behov</h2>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white border border-stone-200 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-stone-900">{results.targetCalories}</div>
                <div className="text-xs text-stone-500">kcal/dag</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-blue-700">{results.protein}g</div>
                <div className="text-xs text-blue-500">protein/dag</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-amber-700">{results.carbs}g</div>
                <div className="text-xs text-amber-500">karbohydrater/dag</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-red-700">{results.fat}g</div>
                <div className="text-xs text-red-500">fett/dag</div>
              </div>
            </div>

            <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 text-xs text-stone-500 space-y-1">
              <div>BMR (hvilestoffskifte): {results.bmr} kcal</div>
              <div>TDEE (totalt daglig forbruk): {results.tdee} kcal</div>
              <div>Justert for mål: {results.targetCalories} kcal</div>
            </div>

            {/* Ukeplan */}
            {results.mealPlan && results.mealPlan.length > 0 && (
              <div>
                <h3 className="font-semibold text-stone-900 text-lg mb-4">Din 7-dagers ukeplan</h3>
                <div className="space-y-3">
                  {results.mealPlan.map(day => (
                    <details key={day.day} className="bg-white border border-stone-200 rounded-xl overflow-hidden group">
                      <summary className="px-4 py-3 cursor-pointer flex items-center justify-between hover:bg-stone-50 transition-colors list-none [&::-webkit-details-marker]:hidden">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-stone-900 text-sm">{day.day}</span>
                          <span className="text-xs text-stone-400">{day.totalKcal} kcal &middot; {day.totalProtein}g protein</span>
                        </div>
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-stone-400 transition-transform group-open:rotate-180"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
                      </summary>
                      <div className="px-4 pb-4 pt-2 border-t border-stone-100 space-y-2">
                        {day.meals.map(meal => (
                          <div key={meal.type} className="flex items-center gap-3 py-1.5">
                            <span className="text-xs text-stone-400 w-14 flex-shrink-0">{meal.type}</span>
                            {meal.recipe ? (
                              <Link href={`/oppskrifter/${meal.recipe.slug}`} className="flex items-center gap-2 flex-1 hover:bg-stone-50 rounded-lg px-2 py-1 -mx-2 transition-colors">
                                <div className="w-8 h-8 rounded-lg bg-stone-100 overflow-hidden flex-shrink-0">
                                  {/* eslint-disable-next-line @next/next/no-img-element */}
                                  <img src={meal.recipe.image} alt="" className="w-full h-full object-cover" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-stone-900 truncate">{meal.recipe.name}</div>
                                  <div className="text-[10px] text-stone-400">{meal.mealKcal} kcal &middot; {meal.mealProtein}g protein{meal.recipe.portions > 1 ? ` · ${meal.recipe.portions} porsj.` : ''}</div>
                                </div>
                              </Link>
                            ) : (
                              <div className="flex-1 text-xs text-stone-400 italic">~{meal.targetKcal} kcal anbefalt</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>
                  ))}
                </div>
                <p className="text-[11px] text-stone-400 mt-3 text-center">
                  Ukeplanen er et forslag basert på dine mål. Juster porsjonsstørrelser etter behov.
                </p>
              </div>
            )}

            {/* CTA */}
            <div className="bg-stone-900 text-white rounded-2xl p-6 text-center">
              <h3 className="font-semibold text-lg mb-2">Vil du ha en coach som tilpasser dette hver uke?</h3>
              <p className="text-stone-400 text-sm mb-4">En kostholdscoach lager en komplett plan tilpasset din hverdag.</p>
              <Link href="/kosthold" className="inline-block px-6 py-2.5 bg-white text-stone-900 rounded-xl text-sm font-medium hover:bg-stone-100 transition-colors">
                Finn din kostholdscoach &rarr;
              </Link>
            </div>

            <Link href="/oppskrifter" className="block text-center text-sm text-stone-400 hover:text-stone-700">
              Se våre oppskrifter &rarr;
            </Link>
          </div>
        )}

        {/* Navigation buttons */}
        {(step as number) < 5 && (
          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <button onClick={() => setStep((step - 1) as Step)}
                className="flex-1 py-3 border border-stone-200 rounded-xl text-sm text-stone-600 hover:bg-stone-50">
                Tilbake
              </button>
            )}
            {step < 4 ? (
              <button onClick={() => canNext() && setStep((step + 1) as Step)} disabled={!canNext()}
                className="flex-1 py-3 bg-stone-900 text-white rounded-xl text-sm font-medium hover:bg-stone-800 disabled:bg-stone-200 disabled:text-stone-400 transition-colors">
                Neste
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={loading}
                className={"flex-1 py-3 text-white rounded-xl text-sm font-medium transition-colors " + (loading ? "bg-stone-300" : "bg-stone-900 hover:bg-stone-800")}>
                {loading ? 'Beregner...' : 'Beregn mitt behov'}
              </button>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
